import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import {
  signInWithEmail,
  signOut as betterAuthSignOut,
  getSession,
  signInWithGoogle as initiateGoogleOAuth,
  handleOAuthCallback as processOAuthCallback,
  getCachedUser,
  getCachedSession,
  hasValidSession,
  clearStoredToken,
} from '@/services/api/auth'
import {
  type BetterAuthUser,
  type BetterAuthSession,
  isAdminUser,
  parseUserRoles,
} from '@/types/betterAuth'
import {
  AuthError,
  AuthErrorType,
  getErrorTranslationKey,
  shouldLogoutOnError,
} from '@/types/authErrors'

// ============================================================================
// Types
// ============================================================================

/**
 * Authentication state using Better Auth.
 * Replaces Supabase auth state.
 */
export interface AuthState {
  /** Current user (null if not authenticated) */
  user: BetterAuthUser | null
  /** Current session (null if not authenticated) */
  session: BetterAuthSession | null
  /** Loading state during auth operations */
  loading: boolean
  /** Authentication error (null if none) */
  error: AuthError | null
  /** Computed: is user authenticated */
  isAuthenticated: boolean
  /** Computed: is user an admin */
  isAdmin: boolean
}

/**
 * Authentication context value with actions.
 */
export interface AuthContextValue extends AuthState {
  /** Sign in with email and password */
  signIn: (email: string, password: string) => Promise<void>
  /** Sign out and clear session */
  signOut: () => Promise<void>
  /** Initiate Google OAuth sign-in */
  signInWithGoogle: () => void
  /** Handle OAuth callback after redirect */
  handleOAuthCallback: () => Promise<void>
  /** Check if user has a specific role */
  hasRole: (role: string) => boolean
  /** Clear current error */
  clearError: () => void
  /** Refresh session from API */
  refreshSession: () => Promise<void>
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * Hook to access auth context.
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<BetterAuthUser | null>(null)
  const [session, setSession] = useState<BetterAuthSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<AuthError | null>(null)

  // ============================================================================
  // Initialization
  // ============================================================================

  useEffect(() => {
    let mounted = true

    async function initializeAuth() {
      try {
        // First, check for cached session (quick check)
        const cachedUser = getCachedUser()
        const cachedSession = getCachedSession()

        if (cachedUser && cachedSession && mounted) {
          // Set cached data for immediate UI
          setUser(cachedUser)
          setSession(cachedSession)
        }

        // Then validate with API if we have a cached session
        if (hasValidSession()) {
          try {
            const sessionResponse = await getSession()
            
            if (!mounted) return

            if (sessionResponse?.user && sessionResponse?.session) {
              setUser(sessionResponse.user)
              setSession(sessionResponse.session)

              // Check if user is banned
              if (sessionResponse.user.banned) {
                Sentry.logger.warn('User is banned, signing out', {
                  userId: sessionResponse.user.id,
                  banReason: sessionResponse.user.banReason,
                })
                await handleSignOut()
                setError(new AuthError(
                  AuthErrorType.USER_BANNED,
                  sessionResponse.user.banReason || 'Your account has been banned.',
                  'USER_BANNED',
                  403
                ))
              }
            } else {
              // No valid session from API
              setUser(null)
              setSession(null)
            }
          } catch (err) {
            Sentry.logger.error('Session validation failed', {
              error: err instanceof Error ? err.message : 'Unknown error',
            })
            // Clear invalid session
            setUser(null)
            setSession(null)
          }
        } else {
          // No cached session
          setUser(null)
          setSession(null)
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { component: 'AuthProvider', action: 'initializeAuth' },
        })
        if (mounted) {
          setUser(null)
          setSession(null)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initializeAuth()

    return () => {
      mounted = false
    }
  }, [])

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Sign out and clear session.
   */
  const handleSignOut = useCallback(async () => {
    try {
      await betterAuthSignOut()
    } catch (err) {
      Sentry.logger.error('Sign out error', {
        error: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      // Always clear local state
      setUser(null)
      setSession(null)
      setError(null)
      clearStoredToken()
    }
  }, [])

  /**
   * Sign in with email and password.
   */
  const handleSignIn = useCallback(async (email: string, password: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await signInWithEmail({ email, password })

      // Check if user is banned
      if (response.user.banned) {
        Sentry.logger.warn('Banned user attempted login', {
          userId: response.user.id,
          banReason: response.user.banReason,
        })
        
        // Clear the session we just created
        await betterAuthSignOut()
        
        throw new AuthError(
          AuthErrorType.USER_BANNED,
          response.user.banReason || 'Your account has been banned.',
          'USER_BANNED',
          403
        )
      }

      // Check if user is an admin (required for admin portal)
      if (!isAdminUser(response.user)) {
        Sentry.logger.warn('Non-admin user attempted admin login', {
          userId: response.user.id,
          roles: response.user.role,
        })
        
        // Clear the session
        await betterAuthSignOut()
        
        throw new AuthError(
          AuthErrorType.UNAUTHORIZED,
          'You do not have permission to access the admin portal.',
          'UNAUTHORIZED',
          403
        )
      }

      setUser(response.user)
      // Better Auth sign-in returns token at root level, construct session object
      setSession({
        token: response.token,
        id: '', // Will be populated on next getSession call
        userId: response.user.id,
        expiresAt: '', // Will be populated on next getSession call  
        createdAt: new Date().toISOString(),
        userAgent: null,
        ipAddress: null,
      })
      
      Sentry.logger.info('User signed in successfully', {
        userId: response.user.id,
        isAdmin: isAdminUser(response.user),
      })
    } catch (err) {
      const authError = err instanceof AuthError 
        ? err 
        : AuthError.fromApiError(err as { code?: string; message?: string; status?: number })

      setError(authError)
      
      Sentry.captureException(authError, {
        tags: { component: 'AuthProvider', action: 'signIn' },
        contexts: {
          auth: {
            errorType: authError.type,
            email: email.replace(/(.{2}).*@/, '$1***@'), // Partially mask email
          },
        },
      })

      throw authError
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Initiate Google OAuth sign-in.
   */
  const handleSignInWithGoogle = useCallback(() => {
    setLoading(true)
    setError(null)
    // This will redirect to Google
    initiateGoogleOAuth()
  }, [])

  /**
   * Handle OAuth callback after redirect.
   */
  const handleOAuthCallbackInternal = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await processOAuthCallback()

      if (!response) {
        // OAuth was cancelled
        throw new AuthError(
          AuthErrorType.OAUTH_CANCELLED,
          'OAuth sign-in was cancelled.',
          'OAUTH_CANCELLED',
          400
        )
      }

      // Check if user is banned
      if (response.user?.banned) {
        await betterAuthSignOut()
        throw new AuthError(
          AuthErrorType.USER_BANNED,
          response.user.banReason || 'Your account has been banned.',
          'USER_BANNED',
          403
        )
      }

      // Check if user is an admin
      if (response.user && !isAdminUser(response.user)) {
        await betterAuthSignOut()
        throw new AuthError(
          AuthErrorType.UNAUTHORIZED,
          'You do not have permission to access the admin portal.',
          'UNAUTHORIZED',
          403
        )
      }

      if (response.user && response.session) {
        setUser(response.user)
        setSession(response.session)
      }
    } catch (err) {
      const authError = err instanceof AuthError 
        ? err 
        : AuthError.fromApiError(err as { code?: string; message?: string; status?: number })

      setError(authError)
      
      Sentry.captureException(authError, {
        tags: { component: 'AuthProvider', action: 'handleOAuthCallback' },
      })

      throw authError
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Refresh session from API.
   */
  const refreshSession = useCallback(async () => {
    try {
      const response = await getSession()
      
      if (response?.user && response?.session) {
        setUser(response.user)
        setSession(response.session)
      } else {
        // Session is invalid
        await handleSignOut()
      }
    } catch (err) {
      if (err instanceof AuthError && shouldLogoutOnError(err.type)) {
        await handleSignOut()
      }
      throw err
    }
  }, [handleSignOut])

  /**
   * Check if user has a specific role.
   */
  const hasRole = useCallback((role: string): boolean => {
    if (!user?.role) return false
    const roles = parseUserRoles(user.role)
    return roles.includes(role.toLowerCase())
  }, [user])

  /**
   * Clear current error.
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: AuthContextValue = {
    user,
    session,
    loading,
    error,
    isAuthenticated: !!user && !!session,
    isAdmin: user ? isAdminUser(user) : false,
    signIn: handleSignIn,
    signOut: handleSignOut,
    signInWithGoogle: handleSignInWithGoogle,
    handleOAuthCallback: handleOAuthCallbackInternal,
    hasRole,
    clearError,
    refreshSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ============================================================================
// Re-exports for convenience
// ============================================================================

export { getErrorTranslationKey }
